import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2, TrendingUp } from "lucide-react";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const sessionId = params.get("session_id");

  useEffect(() => {
    let polls = 0;
    let timer;
    const check = async () => {
      if (!sessionId) { setState("error"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setState("paid");
          setTimeout(() => navigate("/"), 3000);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
        polls += 1;
        if (polls >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(check, 2000);
      } catch {
        polls += 1;
        if (polls >= MAX_POLLS) { setState("error"); return; }
        timer = setTimeout(check, 2000);
      }
    };
    check();
    return () => clearTimeout(timer);
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-[#090D16] text-slate-100 grid place-items-center px-4 grid-bg">
      <div className="bg-[#0F172A] border border-slate-800 rounded-3xl p-10 max-w-md w-full text-center" data-testid="payment-success-card">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-blue-400 mx-auto animate-spin" />
            <h1 className="font-display text-2xl font-bold mt-5">Confirming your payment…</h1>
            <p className="text-slate-400 text-sm mt-2">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/25 grid place-items-center mx-auto">
              <CheckCircle2 className="w-9 h-9 text-emerald-400" />
            </div>
            <h1 className="font-display text-2xl font-bold mt-5">Welcome to Pro! 🎉</h1>
            <p className="text-slate-400 text-sm mt-2">All 15 modules, the AI Tutor and video lessons are now unlocked. Taking you back to the site…</p>
            <button data-testid="pro-continue-btn" onClick={() => navigate("/")} className="mt-6 w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl py-3 transition-colors flex items-center justify-center gap-2">
              <TrendingUp className="w-4 h-4" /> Go to the site now
            </button>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-amber-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-5">Payment processing</h1>
            <p className="text-slate-400 text-sm mt-2">Your payment is still processing. Your Pro access will activate automatically once confirmed.</p>
            <button onClick={() => navigate("/")} className="mt-6 w-full bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl py-3 transition-colors">Continue</button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-red-500/15 border border-red-500/25 grid place-items-center mx-auto">
              <XCircle className="w-9 h-9 text-red-400" />
            </div>
            <h1 className="font-display text-2xl font-bold mt-5">Something went wrong</h1>
            <p className="text-slate-400 text-sm mt-2">We couldn't confirm your payment. If you were charged, your access will update shortly.</p>
            <button onClick={() => navigate("/choose-plan")} className="mt-6 w-full bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl py-3 transition-colors">Back to plans</button>
          </>
        )}
      </div>
    </div>
  );
}
